import "./SectionEdit.scss";
import { useState, useEffect, useContext } from "react";
import {
  Col,
  Row,
  Button,
  Tooltip,
  Modal,
  Icon,
  Input,
  Popconfirm,
  Popover,
  Form,
  Radio,
  Checkbox,
  notification
} from "antd";

import { useTranslation } from "react-i18next";
import { TableEdit, FieldSetEdit, Catalogos, ParagraphEdit, SubsectionEdit, TextEdit, DecisionEdit } from './components'
import { Paragraph, Table, FieldSet, Decision } from "../../FormDeclaration/components";
import { Preview } from '../'
import { formsContext } from "../../../../contexts";
import { getSectionsForRiskValidation, getRiskAssignmentsInTree } from './utils/riskAssignments'

import iconLangEs from '../../../../layouts/Private/components/Footer/assets/lang-es.png'
import iconLangBr from '../../../../layouts/Private/components/Footer/assets/lang-br.png'
import iconLangEn from '../../../../layouts/Private/components/Footer/assets/lang-en.png'

const { TextArea } = Input;

const SectionEdit = ({ form, s, refreshThisSection, closeSection, sectionNro, languages, lang, onChangeLang, handleHasChanges, mode }) => {
  const { t } = useTranslation(['forms', 'translations'])
  const { getFieldDecorator, validateFields, setFieldsValue } = form;
  const { templateVersion, sections } = useContext(formsContext)
  const isKycForm = templateVersion?.template?.tipoDeclaracion === 'KYC'

  const createRiskGuideDemoSection = () => ({
    id: 'RISK_GUIDE_SECTION',
    type: 'CUSTOM',
    title: { [lang]: t('forms.manager.riskGuide.demo.sectionTitle') },
    components: [
      {
        id: 'RISK_GUIDE_FIELDSET',
        type: 'FIELDSET',
        cols: 2,
        hasTitle: true,
        title: { [lang]: t('forms.manager.riskGuide.demo.fieldsetTitle') },
        fields: [{
          id: 'RISK_GUIDE_NATIONALITY',
          type: 'FIELD',
          key: 'field1',
          typeField: 'SELECT',
          title: { [lang]: t('forms.manager.riskGuide.demo.field') },
          required: true,
          tableVisible: true,
          risks: [{ risk: 'ONB-NAT' }]
        }]
      },
      {
        id: 'RISK_GUIDE_DECL',
        type: 'DECL',
        decision: true,
        text: { [lang]: t('forms.manager.riskGuide.demo.declaration') },
        risks: [{ risk: 'ONB-REPR', decision: true, groupId: 'RISK_GUIDE_GROUP', operator: 'OR' }],
        fieldSet: {
          id: 'RISK_GUIDE_DECL_FIELDSET',
          type: 'FIELDSET',
          cols: 2,
          hasTitle: true,
          title: { [lang]: t('forms.manager.riskGuide.demo.fieldsetTitle') },
          fields: [
            { id: 'RISK_GUIDE_DECL_NAME', key: 'field1', type: 'FIELD', typeField: 'INPUT', title: { [lang]: t('forms.manager.riskGuide.demo.declarationName') }, required: true, tableVisible: true },
            { id: 'RISK_GUIDE_DECL_ROLE', key: 'field2', type: 'FIELD', typeField: 'INPUT', title: { [lang]: t('forms.manager.riskGuide.demo.declarationRole') }, required: true, tableVisible: true }
          ]
        }
      },
      {
        id: 'RISK_GUIDE_DECISION',
        type: 'DECISION',
        decision: true,
        text: { [lang]: t('forms.manager.riskGuide.demo.decision') },
        risks: [{ risk: 'ONB-REPR', decision: true, groupId: 'RISK_GUIDE_GROUP', operator: 'OR' }],
        compSi: { id: 'RISK_GUIDE_YES', type: 'SUBSECTION', components: [] },
        compNo: { id: 'RISK_GUIDE_NO', type: 'SUBSECTION', components: [] }
      }
    ]
  })

  const [hasChanges, setHasChanges] = useState(false)
  const [section, setSection] = useState(s)
  const [riskGuideDemoSection, setRiskGuideDemoSection] = useState(null)
  const [isVisiblePreview, setIsVisiblePreview] = useState(false)
  const [isVisibleRiskGuide, setIsVisibleRiskGuide] = useState(false)
  const [riskGuideStep, setRiskGuideStep] = useState(0)
  const [riskGuideTargetRect, setRiskGuideTargetRect] = useState(null)
  const [isVisibleDecisionRisk, setIsVisibleDecisionRisk] = useState(false)
  const [decisionRiskComponent, setDecisionRiskComponent] = useState(null)
  const [selectedDecisionRisk, setSelectedDecisionRisk] = useState('NONE')
  const [selectedDecisionConditions, setSelectedDecisionConditions] = useState([])
  const [selectedDecisionOperator, setSelectedDecisionOperator] = useState('OR')
  const decisionRiskOptions = ['ONB-REPR', 'ONB-PEP', 'ONB-FP', 'ONB-CI']

  const getDecisionRiskComponents = (root) => {
    const visited = new WeakSet()
    const components = []
    const visit = (value, level = 1) => {
      if(!value || typeof value !== 'object' || visited.has(value)) return
      visited.add(value)
      if(Array.isArray(value)) {
        value.forEach((v, i) => visit(v, level+1))
        return
      }
      if(level > 1 && ['DECISION', 'DECL'].includes(value.type) && value.id != null) components.push(value)
      Object.entries(value).forEach(([key, child]) => {
        if(key !== 'risks') visit(child, level+1)
      })
    }
    visit(root)
    return components
  }

  const getDecisionRiskComponentLabel = (component, index) => {
    const text = typeof component.text === 'string'
      ? component.text
      : component.text?.[lang] || component.text?.es || component.text?.en || ''
    const cleanText = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    const fallback = `${getTooltipComponent(component.type) || component.type} ${index + 1}`
    return cleanText ? `${index + 1}. ${cleanText}` : `${index + 1}. ${fallback}`
  }

  const updateDecisionRiskComponents = (updates, sourceSection = section) => {
    const visit = (value) => {
      if(Array.isArray(value)) {
        let changed = false
        const result = value.map(item => {
          const updated = visit(item)
          if(updated !== item) changed = true
          return updated
        })
        return changed ? result : value
      }
      if(!value || typeof value !== 'object') return value

      let result = value
      const update = ['DECISION', 'DECL'].includes(value.type) && value.id != null ? updates[value.id] : null
      if(update) result = { ...result, risks: update }
      Object.keys(value).forEach(key => {
        if(key === 'risks') return
        const updated = visit(value[key])
        if(updated !== value[key]) {
          if(result === value) result = { ...value }
          result[key] = updated
        }
      })
      return result
    }
    const updatedSection = visit(sourceSection)
    if(sourceSection.id === 'RISK_GUIDE_SECTION') {
      setRiskGuideDemoSection(updatedSection)
    } else {
      handleChangeValuesSection(updatedSection)
    }
  }

  useEffect(() => {
    setFieldsValue({title: section.title && section.title[lang]})
    validateFields();
  }, [lang])

  const getComponentByType = (type, add) => {
    if(type === "PARAGRAPH") return { id: getRandomId(), type, text: add ? null:{ [lang]: t('forms.manager.section.exampleText')}, fieldSet: { id: getRandomId(), type: 'FIELDSET', hasTitle: false, fields: []} }
    else if(type === "FIELDSET")  {
      if(add) return {id: getRandomId(), type, cols: 2, hasTitle: true, fields: [{id: getRandomId(), key: 'field1', type: 'FIELD', hasTitle: false, typeField: 'INPUT', required: true}]}
      else return {id: getRandomId(), type, cols: 2, hasTitle: true, fields: [{ id: getRandomId(), type: 'FIELD', typeField: 'INPUT', title: {[lang]: 'Dato1'}, required: true}, {id: getRandomId(), type: 'FIELD', typeField: 'INPUT', title: {[lang]: 'Dato2'}, required: true}]}
    }else if(type === "TABLE") {
      if(add) return { id: getRandomId(), type, fieldSet: { id: getRandomId(), type: 'FIELDSET', cols: 2, hasTitle: false, fields: [{id: getRandomId(), key: 'field1', type: 'FIELD', hasTitle: false, typeField: 'INPUT', required: true, tableVisible: true}] }}
      else return { id: getRandomId(), type, text: {[lang]: t('forms.manager.section.instructionsText')}, fieldSet: { id: getRandomId(), type: 'FIELDSET', cols: 2, hasTitle: true, title: {[lang]: t('forms.manager.section.fieldsTitle')}, fields: [{id: getRandomId(), key: 'field1', type: 'FIELD', typeField: 'INPUT', title: {[lang]: 'Dato1'}, required: true, tableVisible: true}, { id: getRandomId(), key: 'field2', type: 'FIELD', title: {[lang]: 'Dato2'}, typeField: 'INPUT', required: true, tableVisible: true}] }}
    }else if(type === "DECL") {
      if(add) return { id: getRandomId(), type, fieldSet: { id: getRandomId(), type: 'FIELDSET', cols: 2, hasTitle: false, fields: [{id: getRandomId(), key: 'field1', type: 'FIELD', hasTitle: false, typeField: 'INPUT', required: true, tableVisible: true}] }}
      else return { id: getRandomId(), type, decision: true, text: {[lang]: t('forms.manager.section.instructionsText')}, fieldSet: { id: getRandomId(), type: 'FIELDSET', cols: 2, hasTitle: true, title: {[lang]: t('forms.manager.section.fieldsTitle')}, fields: [{id: getRandomId(), key: 'field1', type: 'FIELD', typeField: 'INPUT', title: {[lang]: t('forms.manager.section.data1')}, required: true, tableVisible: true}, { id: getRandomId(), key: 'field2', type: 'FIELD', title: {[lang]: t('forms.manager.section.data2')}, typeField: 'INPUT', required: true, tableVisible: true}] }}
    }else if(type === "TEXT") {
      return {id: getRandomId(), type, required: false, hasTitle: false}
    }else if(type === "SUBSECTION") {
      return { id: getRandomId(), type, components: []}
    }else if(type === "DECISION") {
      return { id: getRandomId(), type, text: add?null:{[lang]: t('forms.manager.section.instructionsText')}, compSi: getComponentByType('SUBSECTION', add), compNo: getComponentByType('SUBSECTION', add)}
    }else if(type === "COMMENTS") {
    }
  }

  const refreshSection = (s, invalidate=false) => {
    let _s = {...s}
    if(invalidate) {
      let langSections = _s.languages??{}
      for(let l of languages) {
        langSections[l] = { valid: false }
      }
      _s.languages = langSections
    }
    setSection(_s)
    setHasChanges(true)
    handleHasChanges(true)
    validateFields()
  }

  const handleChangeTitle = (value) => {
      let title = section.title??{}
      title[lang] = value
      refreshSection({...section, title})
  }

  const handleChangeCatalogoActive = (cats) => {
    let comp = []
    let invalidate = false
    cats.map(c => {
      if(c.active) {
        let _comp = { ...c, type: 'FIELDSET', id: getRandomId()}
        _comp.fields = _comp.fields.filter(f => f.active).map(f => {
          return { ...f, typeField: f.type, type: 'FIELD', id: getRandomId()}
        })
        if(_comp.fields.length > 0) {
          comp.push(_comp)
          invalidate = true
        }
      }
    })
    let _s = { ...section, components: comp }
    refreshSection(_s, invalidate)
  }

  const getRandomId = () => {
    return 'R' + Math.floor(Math.random() * 1000000);
  }

  const handlerChangeCatalogo = (_c) => {
    let _fieldset = []
    let invalidate = false
    _c.map(c => {
      let fields = c.fields.filter(f => f.active)
      let _fields = []
      if(fields.length > 0) {
        fields.map(f => {
          let _f = { ...f, typeField: f.type, type: 'FIELD', id: getRandomId() }
          _fields.push(_f)
          invalidate = true
        })
        let exists = false
        section.components && section.components.map((fs, index) => {
          if(fs.type === 'FIELDSET') {
            if(fs.key === c.key) {
              exists = true
              let _fs = { ...fs, fields: _fields }              
              _fieldset.push(_fs)
              invalidate = true
            }
          }
        })
        if(!exists) {
          let _fs = { ...c, type: 'FIELDSET', fields: _fields}
          _fieldset.push(_fs)
          invalidate = true
        }
      }
    })
    let _s = { ...section, components: _fieldset}
    refreshSection(_s, invalidate)
  }

  const getStrLanguage = (l) => {
    if(l === "es") return t('forms.language.es')
    else if(l === "en") return t('forms.language.en')
    else if(l === "pt") return t('forms.language.pt')
  }

  const saveThisSection = () => {
    validateFields((errors, fields) => {
      let langSections = section.languages??{}
      if(!errors) {
        langSections[lang] = { valid: true }
        let sec = {...section, languages: langSections}
        setSection(sec)
        let isValid = true
        for(let l of languages) {
          if(l !== lang) {
            if(!(langSections[l] && langSections[l].valid)) {
              isValid = false
              notification.error({
                message: t('forms.errors.error'),
                description: t('forms.errors.missingLanguageData', { language: getStrLanguage(l) })
              })
              break
            }
          }
        }
        if(isValid) {
          refreshThisSection(sec)
          setHasChanges(false)
          handleHasChanges(true)
        }
      }else {
        langSections[lang] = { valid: false }
        let sec = {...section, languages: langSections}
        setSection(sec)
        notification.error({
          message: t('forms.errors.error'),
          description: t('messages.aml.missingRequiredField', {ns: 'translations'})
        })
      }
    });
  }

  const handlePreviewSection = () => {
    setIsVisiblePreview(true)
  }

  const openRiskGuide = () => {
    setRiskGuideDemoSection(createRiskGuideDemoSection())
    setRiskGuideStep(0)
    setIsVisibleRiskGuide(true)
  }

  const closeRiskGuide = () => {
    setIsVisibleRiskGuide(false)
    setRiskGuideStep(0)
    setRiskGuideTargetRect(null)
    setRiskGuideDemoSection(null)
    setIsVisibleDecisionRisk(false)
    setDecisionRiskComponent(null)
    setSelectedDecisionConditions([])
    setSelectedDecisionOperator('OR')
  }

  const riskGuideSteps = [
    {
      target: '[data-risk-guide-target="demo-toolbar"]',
      fallback: '[data-risk-guide-target="preview-example"]',
      title: t('forms.manager.riskGuide.step1.title'),
      description: t('forms.manager.riskGuide.step1.description')
    },
    {
      target: '[data-risk-guide-target="field-type"]',
      fallback: '[data-risk-guide-target="preview-example"]',
      title: t('forms.manager.riskGuide.step2.typeTitle'),
      description: t('forms.manager.riskGuide.step2.typeDescription')
    },
    {
      target: '[data-risk-guide-target="field-risk"]:not([disabled])',
      fallback: '[data-risk-guide-target="field-type"]',
      title: t('forms.manager.riskGuide.step2.title'),
      description: t('forms.manager.riskGuide.step2.description')
    },
    {
      target: '[data-risk-guide-target="decision-risk"]',
      fallback: '[data-risk-guide-target="demo-toolbar"]',
      title: t('forms.manager.riskGuide.step3.title'),
      description: t('forms.manager.riskGuide.step3.description')
    },
    {
      target: '[data-risk-guide-target="preview"]',
      title: t('forms.manager.riskGuide.preview.title'),
      description: t('forms.manager.riskGuide.preview.description')
    }
  ]

  useEffect(() => {
    if(!isVisibleRiskGuide) return undefined
    const step = riskGuideSteps[riskGuideStep]
    const target = document.querySelector(step.target) ||
      (step.fallback && document.querySelector(step.fallback)) ||
      (step.fallback2 && document.querySelector(step.fallback2))
    if(!target) {
      setRiskGuideTargetRect(null)
      return undefined
    }
    target.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })
    const updateTargetPosition = () => {
      const rect = target.getBoundingClientRect()
      setRiskGuideTargetRect({ top: rect.top, left: rect.left, width: rect.width, height: rect.height })
    }
    updateTargetPosition()
    window.addEventListener('resize', updateTargetPosition)
    window.addEventListener('scroll', updateTargetPosition, true)
    return () => {
      window.removeEventListener('resize', updateTargetPosition)
      window.removeEventListener('scroll', updateTargetPosition, true)
    }
  }, [isVisibleRiskGuide, riskGuideStep, section, riskGuideDemoSection])

  const getRiskGuideCardStyle = () => {
    if(!riskGuideTargetRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }
    const isFieldTypeStep = riskGuideStep === 1
    const cardWidth = Math.min(440, window.innerWidth - 32)
    const cardHeight = isFieldTypeStep ? 360 : riskGuideStep === 4 ? 300 : 240
    const right = riskGuideTargetRect.left + riskGuideTargetRect.width + 16
    const leftOfTarget = riskGuideTargetRect.left - cardWidth - 16
    const hasRoomRight = right + cardWidth <= window.innerWidth - 16
    const hasRoomLeft = leftOfTarget >= 16

    if(riskGuideStep === 1 && (hasRoomRight || hasRoomLeft)) {
      const left = hasRoomRight ? right : leftOfTarget
      const top = Math.max(16, Math.min(riskGuideTargetRect.top, window.innerHeight - cardHeight - 16))
      return { top, left, width: cardWidth, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }
    }

    const below = riskGuideTargetRect.top + riskGuideTargetRect.height + 16
    const above = riskGuideTargetRect.top - cardHeight - 16
    const top = below + cardHeight <= window.innerHeight - 16
      ? below
      : Math.max(16, above)
    const left = Math.max(16, Math.min(riskGuideTargetRect.left, window.innerWidth - cardWidth - 16))
    return { top, left, width: cardWidth, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }
  }

  const showRiskGuideDemo = isKycForm && isVisibleRiskGuide
  const renderRiskGuideDemo = () => (
    <>
      <div className="form-risk-guide-demo-notice">
        <Icon type="info-circle" /> {t('forms.manager.riskGuide.demo.notice')}
      </div>
      <Row className="custom-tools">
        <ul className="custom-tools-group-menu" data-risk-guide-target="demo-toolbar">
          {['FIELDSET', 'DECL', 'DECISION'].map(type =>
            <li key={type}>
              <a href="#0" onClick={event => {
                event.preventDefault()
                const component = getComponentByType(type, true)
                setRiskGuideDemoSection(current => ({ ...current, components: [...current.components, component] }))
              }}>
                <span>
                  {type === 'FIELDSET'
                    ? <><Icon type="form" />&nbsp;{t('forms.manager.section.tooltip.data')}</>
                    : type === 'DECISION'
                      ? <><Icon type="fork" />&nbsp;{t('forms.manager.section.tooltip.decision')}</>
                      : <><Icon type="table" />&nbsp;{t('forms.manager.section.tooltip.declaration')}</>}
                </span>
                <span><Icon type="plus" /></span>
              </a>
            </li>
          )}
        </ul>
      </Row>
    </>
  )

  const sectionForComponentRender = showRiskGuideDemo ? riskGuideDemoSection : section
  const componentsForRender = sectionForComponentRender?.components
  const handleChangeRiskGuideDemoSection = (component) => {
    const replaceComponent = (value) => {
      if(Array.isArray(value)) {
        const updatedValues = value.map(replaceComponent)
        return updatedValues.some((item, index) => item !== value[index]) ? updatedValues : value
      }
      if(!value || typeof value !== 'object') return value
      if(component.id != null && value.id === component.id) return component

      let updated = value
      Object.keys(value).forEach(key => {
        if(key === 'risks') return
        const child = value[key]
        if(child && typeof child === 'object') {
          const updatedChild = replaceComponent(child)
          if(updatedChild !== child) {
            if(updated === value) updated = { ...value }
            updated[key] = updatedChild
          }
        }
      })
      return updated
    }

    const updatedSection = component.id === riskGuideDemoSection?.id
      ? component
      : replaceComponent(riskGuideDemoSection)
    setRiskGuideDemoSection({
      ...updatedSection,
      components: [...(updatedSection.components || [])]
    })
  }

  const closeModalHandler = () => {
    setIsVisiblePreview(false)
  }

  const handleChangeValuesSection = (component, invalidate) => {
    const replaceComponent = (value) => {
      if(Array.isArray(value)) {
        const replacedValues = value.map(replaceComponent)
        return replacedValues.some((item, index) => item !== value[index]) ? replacedValues : value
      }
      if(!value || typeof value !== 'object') {
        return value
      }
      if(component.id != null && value.id === component.id) {
        return component
      }

      let updated = value
      Object.keys(value).forEach(key => {
        const child = value[key]
        if(child && typeof child === 'object') {
          const replacedChild = replaceComponent(child)
          if(replacedChild !== child) {
            if(updated === value) updated = { ...value }
            updated[key] = replacedChild
          }
        }
      })
      return updated
    }

    const updatedSection = component.id === section.id ? component : replaceComponent(section)
    refreshSection(updatedSection, invalidate)
  }

  const showDecisionRiskSelector = (component) => {
    const currentSection = component?.id?.startsWith('RISK_GUIDE_') ? riskGuideDemoSection : section
    const selected = Array.isArray(component.risks)
      ? component.risks.find(item => decisionRiskOptions.includes(item.risk))
      : null
    const groupMembers = selected?.groupId
      ? getDecisionRiskComponents(currentSection).filter(item =>
          Array.isArray(item.risks) && item.risks.some(risk => risk?.risk === selected.risk && risk.groupId === selected.groupId))
      : []
    const groupComponents = groupMembers.length > 0 ? groupMembers : [component]
    const getCondition = item => Array.isArray(item.risks)
      ? item.risks.find(risk => risk?.risk === selected?.risk && (!selected?.groupId || risk.groupId === selected.groupId))
      : null
    setDecisionRiskComponent(component)
    setSelectedDecisionRisk(selected ? selected.risk : 'NONE')
    setSelectedDecisionConditions(groupComponents.map(item => ({
      componentId: item.id,
      decision: typeof getCondition(item)?.decision === 'boolean' ? getCondition(item).decision : true
    })))
    setSelectedDecisionOperator(selected?.operator === 'OR' ? 'OR' : 'AND')
    setIsVisibleDecisionRisk(true)
  }

  const closeDecisionRiskSelector = () => {
    setDecisionRiskComponent(null)
    setSelectedDecisionConditions([])
    setSelectedDecisionOperator('OR')
    setIsVisibleDecisionRisk(false)
  }

  const getCurrentDecisionRiskAssignment = () => {
    return (Array.isArray(decisionRiskComponent?.risks) ? decisionRiskComponent.risks : [])
      .find(item => decisionRiskOptions.includes(item.risk))
  }

  const getCurrentDecisionRiskGroupId = () => getCurrentDecisionRiskAssignment()?.groupId

  const isDecisionRiskAssignedElsewhere = (risk) => {
    const currentSection = decisionRiskComponent?.id?.startsWith('RISK_GUIDE_') ? riskGuideDemoSection : section
    const currentAssignment = getCurrentDecisionRiskAssignment()
    const currentGroupId = currentAssignment?.risk === risk ? currentAssignment.groupId : null
    const source = decisionRiskComponent?.id?.startsWith('RISK_GUIDE_')
      ? currentSection
      : getSectionsForRiskValidation(sections, currentSection)
    return getRiskAssignmentsInTree(source, risk)
      .some(({ node, item }) => node.id !== decisionRiskComponent?.id && (!currentGroupId || item?.groupId !== currentGroupId))
  }

  const isDecisionRiskOptionDisabled = (risk) => {
    const currentComponentHasRisk = (Array.isArray(decisionRiskComponent?.risks) ? decisionRiskComponent.risks : [])
      .some(item => item?.risk === risk)
    if(currentComponentHasRisk) return false
    return isDecisionRiskAssignedElsewhere(risk)
  }

  const hasConflictingRisk = (component, risk, groupId) =>
    (Array.isArray(component.risks) ? component.risks : []).some(item =>
      decisionRiskOptions.includes(item?.risk) && item?.groupId !== groupId)

  const getAvailableDecisionRiskComponents = (risk) => {
    const currentSection = decisionRiskComponent?.id?.startsWith('RISK_GUIDE_') ? riskGuideDemoSection : section
    return getDecisionRiskComponents(currentSection).filter(item =>
    item.id === decisionRiskComponent?.id || !hasConflictingRisk(item, risk, getCurrentDecisionRiskGroupId()))
  }

  const saveDecisionRisk = () => {
    const currentSection = decisionRiskComponent?.id?.startsWith('RISK_GUIDE_') ? riskGuideDemoSection : section
    const currentGroupId = getCurrentDecisionRiskGroupId()
    const selectedConditions = selectedDecisionConditions
      .filter(condition => condition && condition.componentId != null)
    const selectedComponentIds = selectedConditions.map(condition => condition.componentId)

    if(selectedDecisionRisk !== 'NONE' && selectedConditions.length === 0) {
      notification.error({
        message: t('forms.errors.error'),
        description: t('forms.manager.riskGroup.mustSelectComponent')
      })
      return
    }

    if(selectedDecisionRisk !== 'NONE' && isDecisionRiskAssignedElsewhere(selectedDecisionRisk)) {
      notification.error({
        message: t('forms.errors.error'),
        description: t('forms.manager.riskAlreadyAssigned', {
          risk: t(`messages.aml.titles.${selectedDecisionRisk}`, {ns: 'translations'})
        })
      })
      return
    }

    const components = getDecisionRiskComponents(currentSection)
    const selectedComponents = components.filter(item => selectedComponentIds.includes(item.id))
    const currentAssignment = getCurrentDecisionRiskAssignment()
    const groupId = selectedDecisionRisk === 'NONE' ? null :
      (currentAssignment?.risk === selectedDecisionRisk && currentGroupId ? currentGroupId : `RG${getRandomId()}`)
    const conflictingComponent = selectedDecisionRisk !== 'NONE'
      ? selectedComponents.find(item => item.id !== decisionRiskComponent.id && hasConflictingRisk(item, selectedDecisionRisk, currentGroupId))
      : null
    if(conflictingComponent) {
      notification.error({
        message: t('forms.errors.error'),
        description: t('forms.manager.riskAlreadyAssigned', {
          risk: t(`messages.aml.titles.${selectedDecisionRisk}`, {ns: 'translations'})
        })
      })
      return
    }

    const affectedComponents = components.filter(item =>
      item.id === decisionRiskComponent.id ||
      (currentGroupId && Array.isArray(item.risks) && item.risks.some(risk => risk?.groupId === currentGroupId)) ||
      selectedComponentIds.includes(item.id))
    const updates = {}
    affectedComponents.forEach(item => {
      const originalRisks = Array.isArray(item.risks) ? item.risks : []
      let risks = originalRisks.filter(risk => {
        if(currentGroupId && risk?.groupId === currentGroupId) return false
        if(item.id === decisionRiskComponent.id && decisionRiskOptions.includes(risk?.risk)) return false
        if(selectedDecisionRisk !== 'NONE' && selectedComponentIds.includes(item.id) && decisionRiskOptions.includes(risk?.risk)) return false
        return true
      })

      const condition = selectedConditions.find(value => value.componentId === item.id)
      if(selectedDecisionRisk !== 'NONE' && condition) {
        risks.push({
          risk: selectedDecisionRisk,
          decision: condition.decision,
          groupId,
          operator: selectedDecisionOperator
        })
      }
      updates[item.id] = risks
    })

    updateDecisionRiskComponents(updates, currentSection)
    closeDecisionRiskSelector()
  }

  const toggleDecisionRiskCondition = (componentId, checked) => {
    setSelectedDecisionConditions(current => {
      const existing = current.find(condition => condition.componentId === componentId)
      if(checked && !existing) return [...current, { componentId, decision: true }]
      if(!checked) return current.filter(condition => condition.componentId !== componentId)
      return current
    })
  }

  const updateDecisionRiskCondition = (componentId, decision) => {
    setSelectedDecisionConditions(current => current.map(condition =>
      condition.componentId === componentId ? { ...condition, decision } : condition))
  }

  const getDecisionRiskTooltip = (component) => {
    const selected = Array.isArray(component.risks)
      ? component.risks.find(item => decisionRiskOptions.includes(item.risk))
      : null
    if(!selected) return `${t('messages.aml.cdi.dashboard.riesgo', {ns: 'translations'})}: ${t('messages.aml.none', {ns: 'translations'})}`
    if(selected.groupId) {
      const sourceSection = component?.id?.startsWith('RISK_GUIDE_') ? riskGuideDemoSection : section
      const groupSize = getRiskAssignmentsInTree(sourceSection, selected.risk)
        .filter(({ item }) => item?.groupId === selected.groupId).length
      const operator = t(selected.operator === 'OR' ? 'forms.manager.riskGroup.any' : 'forms.manager.riskGroup.all')
      return `${t('messages.aml.cdi.dashboard.riesgo', {ns: 'translations'})}: ${t(`messages.aml.titles.${selected.risk}`, {ns: 'translations'})} · ${operator} (${groupSize})`
    }
    const decisionText = t(selected.decision === false ? 'forms.options.no' : 'forms.options.si')
    return `${t('messages.aml.cdi.dashboard.riesgo', {ns: 'translations'})}: ${t(`messages.aml.titles.${selected.risk}`, {ns: 'translations'})} · ${decisionText}`
  }

  const getTooltipComponent = (c) => {
    if(c === 'PARAGRAPH') return t('forms.manager.section.tooltip.paragraph');
    else if(c === 'FIELDSET') return t('forms.manager.section.tooltip.data');
    else if(c === 'TABLE') return t('forms.manager.section.tooltip.table');
    else if(c === 'DECL') return t('forms.manager.section.tooltip.declaration');
    else if(c === 'TEXT') return t('forms.manager.section.tooltip.text');
    else if(c === 'SUBSECTION') return t('forms.manager.section.tooltip.subsection');
    else if(c === 'COMMENTS') return t('forms.manager.section.tooltip.comments');
    else if(c === 'DECISION') return t('forms.manager.section.tooltip.decision');
  }

  const handleClickComponent = (c) => {
    let comp = []
    section.components.map(c => {
      comp.push(c)
    })
    comp.push(getComponentByType(c, true))

    let _s = { ...section, components:  comp }
    refreshSection(_s, true)
  }

  const deleteComponent = (index) => {
    let comp = section.components.filter((c,i) => index !== i)
    let _s = { ...section, components:  comp }
    refreshSection(_s)
  }

  const getTextComponent = (comp) => {
    if(comp === 'PARAGRAPH') return t('forms.manager.section.description.paragraph')
    else if(comp === 'FIELDSET') return t('forms.manager.section.description.data')
    else if(comp === 'TABLE') return t('forms.manager.section.description.table')
    else if(comp === 'DECL') return t('forms.manager.section.description.declaration')
    else if(comp === 'TEXT') return t('forms.manager.section.description.text')
    else if(comp === 'SUBSECTION') return t('forms.manager.section.description.subsection')
    else if(comp === 'COMMENTS') return t('forms.manager.section.description.comments')
    else if(comp === 'DECISION') return t('forms.manager.section.description.decision')
  }

  const getContentPopOver = (comp) => {
    return <Row className="overlayExample">
      <Col className="explain-component" span={24}>{getTextComponent(comp)}</Col>
      <Col className="component-example"span={24}>
      { comp === 'PARAGRAPH' && 
        <Paragraph lang={lang} component={getComponentByType(comp)} mode="preview" />
      }
      { comp === 'FIELDSET' && 
        <FieldSet section={section} parent={section} lang={lang} component={getComponentByType(comp)} mode="preview" />
      }
      { (comp === 'TABLE' || comp === 'DECL') && 
        <Table lang={lang} section={section} component={getComponentByType(comp)} mode="preview" />
      }
      { comp === 'TEXT' && 
        <TextArea rows={4} value='' disabled={true} />
      }
      { comp === 'DECISION' && 
        <Decision section={section} lang={lang} component={getComponentByType(comp)} mode="preview" />
      }
      </Col>
    </Row>
  }

  const handleCloseSection = () => {
    if(hasChanges) {
      Modal.confirm({
          title: t("messages.aml.confirm", {ns: 'translations'}),
          content: t('messages.aml.params.confirmation.leave', {ns: 'translations'}),
          okText: t('messages.aml.confirm', {ns: 'translations'}),
          cancelText: t('messages.aml.cancel', {ns: 'forms'}),
          onOk: () => {
              closeSection(false)
          }
      });
    }else {
      closeSection()
    }
  }

  return (
    <div className="form-manager-section-edit">
      <Row>
        {sectionNro &&
          <Col className="section-top">{t('forms.manager.section.label', {num: sectionNro})}</Col>
        }
        <Col span={8} md={8} sm={10} xs={10}>
          <h2>{sectionNro ? ((section.title && section.title[lang])??"["+t('forms.manager.section.title.placeholder')+"]") : section.name }</h2>
        </Col>
        { languages &&
          <Col span={8} md={8} sm={14} xs={14} style={{textAlign:'center'}}>
            <label>{t('forms.manager.section.language')}: </label>
              { lang === "all" ?
                <b>{t('forms.manager.section.language.undefined')}</b>
                :            
                <Radio.Group onChange={onChangeLang} defaultValue={lang} buttonStyle="solid" size="small">
                    { languages.includes("es") && <Radio.Button value="es"><img className="language-icon" src={ iconLangEs } /></Radio.Button> }
                    { languages.includes("en") && <Radio.Button value="en"><img className="language-icon" src={ iconLangEn } /></Radio.Button> }
                    { languages.includes("pt") && <Radio.Button value="pt"><img className="language-icon" src={ iconLangBr } /></Radio.Button> }
                </Radio.Group>
              }
          </Col>
        }
        { !sectionNro &&
          <Col span={8} md={8} sm={24} xs={24} className="tools">
            <Button onClick={saveThisSection} type="primary" disabled={!hasChanges}>{t('forms.manager.save')}</Button>
            <Button onClick={handleCloseSection}>{t('forms.manager.section.back')}</Button>
            <Button data-risk-guide-target="preview" onClick={handlePreviewSection} type="primary">{t('forms.manager.section.preview')}</Button>
          </Col>
        }
      </Row>
      {isKycForm && mode === "edit" &&
        <Row className="section-risk-guide-trigger">
          <Col span={24}>
            <Button type="link" size="small" icon="question-circle" onClick={openRiskGuide}>
              {t('forms.manager.riskGuide.open')}
            </Button>
          </Col>
        </Row>
      }
      { !sectionNro &&
        <Row style={{marginBottom:5}}>
          <Col>
            { refreshThisSection ?
              <Form.Item label={t("forms.manager.section.new.title.placeholder")}>
              { getFieldDecorator('title', {
                  initialValue: section.title && section.title[lang],
                  rules:
                      [
                          { required: true, message: t('messages.aml.requestedField', {ns: 'translations'}) }
                      ]
                  })(
                      <Input onChange={(e) => handleChangeTitle(e.target.value)}/>
                  )
              }
              </Form.Item>
              :
              <Input value={section.title && section.title[lang]} disabled={true} placeholder={t("forms.manager.section.new.title.placeholder")} />
            }
          </Col>
        </Row>
      }
      { !sectionNro &&
      <>
        { (section.type === 'CONTACTPERSON' || section.type === 'CONTACTENTITY') &&
          <Catalogos handleChangeCatalogoActive={handleChangeCatalogoActive} handlerChangeCatalogo={handlerChangeCatalogo}
            section={section} type={section.type}
          />
        }
        { section.type === 'CUSTOM' && !showRiskGuideDemo &&
          <Row className="custom-tools">
            <ul className="custom-tools-group-menu" data-risk-guide-target="component-toolbar">
            {["SUBSECTION", "PARAGRAPH", "FIELDSET", "DECL", "TABLE", "DECISION", "TEXT"].map(c =>
              <Popover content={getContentPopOver(c)} title={getTooltipComponent(c)} trigger="hover" placement="bottom">
                <li data-risk-guide-target={c === 'FIELDSET' ? 'add-fieldset' : c === 'DECISION' ? 'add-decision' : undefined}>
                    <a href="#0" onClick={() => handleClickComponent(c)}>
                      <span>
                        { c === "PARAGRAPH" && <><Icon type="align-center" />&nbsp;{t('forms.manager.section.tooltip.paragraph')}</>}
                        { c === "FIELDSET" && <><Icon type="form" />&nbsp;{t('forms.manager.section.tooltip.data')}</>}
                        { c === "TABLE" && <><Icon type="table" />&nbsp;{t('forms.manager.section.tooltip.table')}</>}
                        { c === "DECL" && <><Icon type="table" />&nbsp;{t('forms.manager.section.tooltip.declaration')}</>}
                        { c === "TEXT" && <><Icon type="edit" />&nbsp;{t('forms.manager.section.tooltip.text')}</>}
                        { c === "SUBSECTION" && <><Icon type="profile" />&nbsp;{t('forms.manager.section.tooltip.subsection')}</>}
                        { c === "DECISION" && <><Icon type="fork" />&nbsp;{t('forms.manager.section.tooltip.decision')}</>}
                      </span>
                      <span>
                        <Tooltip title={t('forms.manager.section.tooltip.add')}>
                          <Icon type="plus"/>
                        </Tooltip>
                      </span>
                    </a>
                </li>
              </Popover>
              )}
            </ul>
          </Row>
        }

        <div className={'section-components section-type-'+section.type+(showRiskGuideDemo ? ' form-risk-guide-demo-section' : '')} data-risk-guide-target={showRiskGuideDemo ? 'preview-example' : undefined}>
          {showRiskGuideDemo && renderRiskGuideDemo()}
          { section.components && (section.type === 'CONTACTPERSON' || section.type === 'CONTACTENTITY') &&
            <Row><h4>{t('forms.manager.section.selectedData')}</h4></Row>
          }
          { componentsForRender && componentsForRender.map((component, index) =>
            <Row className={'row-component-section row-' + component.type}>
              <Row className="header-section-component-custom">
                <Col span={20}><strong>{index+1}. {getTooltipComponent(component.type)}</strong></Col>
                <Col span={4} className="tools-section-component-custom">
                  { isKycForm && ['DECISION', 'DECL'].includes(component.type) &&
                    <Tooltip title={getDecisionRiskTooltip(component)} placement="bottom">
                      <Button data-risk-guide-target="decision-risk" size="small" icon="safety" onClick={() => showDecisionRiskSelector(component)} />
                    </Tooltip>
                  }
                  {section.type === 'CUSTOM' && !showRiskGuideDemo &&
                    <Tooltip title={t('forms.manager.section.tooltip.deleteComponent')} placement="bottom">
                      <Popconfirm
                        title={t('forms.manager.section.tooltip.confirmDeleteComponent', {ns: 'forms'})}
                        onConfirm={(e) => deleteComponent(index)}
                        cancelText={t('messages.aml.cancel', {ns: 'forms'})}
                      >
                        <Button size="small" icon="delete" />
                      </Popconfirm>
                    </Tooltip>
                  }
                </Col>
              </Row>
              { component.type === 'PARAGRAPH' &&
                <ParagraphEdit form={form} section={sectionForComponentRender} component={component} fieldset={component.fieldSet} lang={lang} handleChangeValuesSection={showRiskGuideDemo ? handleChangeRiskGuideDemoSection : handleChangeValuesSection} />
              }
              { component.type === 'FIELDSET' &&
                <FieldSetEdit form={form} section={sectionForComponentRender} component={component} fieldset={component} lang={lang} handleChangeValuesSection={showRiskGuideDemo ? handleChangeRiskGuideDemoSection : handleChangeValuesSection} isRiskGuideDemo={showRiskGuideDemo} />
              }
              { (component.type === 'TABLE' || component.type === 'DECL') &&
                <TableEdit form={form} section={sectionForComponentRender} component={component} fieldset={component.fieldSet} lang={lang} handleChangeValuesSection={showRiskGuideDemo ? handleChangeRiskGuideDemoSection : handleChangeValuesSection} isRiskGuideDemo={showRiskGuideDemo} />
              }
              { component.type === 'TEXT' &&
                <TextEdit form={form} section={sectionForComponentRender} component={component} lang={lang} handleChangeValuesSection={showRiskGuideDemo ? handleChangeRiskGuideDemoSection : handleChangeValuesSection}/>
              }
              { component.type === 'SUBSECTION' &&
                <SubsectionEdit form={form} indexSection={index} section={sectionForComponentRender} component={component} subsection={component} lang={lang}
                  handleChangeValuesSection={showRiskGuideDemo ? handleChangeRiskGuideDemoSection : handleChangeValuesSection}
                  getComponentByType={getComponentByType} getTooltipComponent={getTooltipComponent}
                  isKycForm={isKycForm} getDecisionRiskTooltip={getDecisionRiskTooltip}
                  showDecisionRiskSelector={showDecisionRiskSelector} />
              }
              { component.type === 'DECISION' &&
                <DecisionEdit form={form} section={sectionForComponentRender} component={component} lang={lang}
                  handleChangeValuesSection={showRiskGuideDemo ? handleChangeRiskGuideDemoSection : handleChangeValuesSection}
                  getComponentByType={getComponentByType} getTooltipComponent={getTooltipComponent}
                  isKycForm={isKycForm} getDecisionRiskTooltip={getDecisionRiskTooltip}
                  showDecisionRiskSelector={showDecisionRiskSelector}
                />
              }
            </Row>
          )}
        </div>
      </>
      }
      {sectionNro && showRiskGuideDemo &&
        <div className={'section-components section-type-'+section.type+' form-risk-guide-demo-section'} data-risk-guide-target="preview-example">
          {renderRiskGuideDemo()}
          {componentsForRender && componentsForRender.map((component, index) =>
            <Row key={component.id} className={'row-component-section row-' + component.type}>
              <Row className="header-section-component-custom">
                <Col span={20}><strong>{index + 1}. {getTooltipComponent(component.type)}</strong></Col>
                <Col span={4} className="tools-section-component-custom">
                  {['DECISION', 'DECL'].includes(component.type) &&
                    <Tooltip title={getDecisionRiskTooltip(component)} placement="bottom">
                      <Button data-risk-guide-target="decision-risk" size="small" icon="safety" onClick={() => showDecisionRiskSelector(component)} />
                    </Tooltip>
                  }
                </Col>
              </Row>
              {component.type === 'FIELDSET' &&
                <FieldSetEdit form={form} section={sectionForComponentRender} component={component} fieldset={component} lang={lang} handleChangeValuesSection={handleChangeRiskGuideDemoSection} isRiskGuideDemo />
              }
              {component.type === 'DECL' &&
                <TableEdit form={form} section={sectionForComponentRender} component={component} fieldset={component.fieldSet} lang={lang} handleChangeValuesSection={handleChangeRiskGuideDemoSection} isRiskGuideDemo />
              }
              {component.type === 'DECISION' &&
                <DecisionEdit form={form} section={sectionForComponentRender} component={component} lang={lang}
                  handleChangeValuesSection={handleChangeRiskGuideDemoSection}
                  getComponentByType={getComponentByType} getTooltipComponent={getTooltipComponent}
                  isKycForm={isKycForm} getDecisionRiskTooltip={getDecisionRiskTooltip}
                  showDecisionRiskSelector={showDecisionRiskSelector}
                />
              }
            </Row>
          )}
        </div>
      }
      { isVisibleDecisionRisk &&
        <Modal
          className="modal-risk-rules"
          title={t('messages.aml.cdi.dashboard.riesgo', {ns: 'translations'})}
          visible={true}
          centered
          onOk={saveDecisionRisk}
          onCancel={closeDecisionRiskSelector}
          okText={t('messages.aml.save', {ns: 'translations'})}
          cancelText={t('messages.aml.cancel', {ns: 'translations'})}
          width={700}
        >
          <Radio.Group
            value={selectedDecisionRisk}
            onChange={event => setSelectedDecisionRisk(event.target.value)}
          >
            <Row>
              <Col span={24} style={{padding: '6px 0'}}>
                <Radio value="NONE">{t('messages.aml.none', {ns: 'translations'})}</Radio>
              </Col>
              {decisionRiskOptions.map(code =>
                <Col span={24} key={code} style={{padding: '6px 0'}}>
                  <Radio
                    value={code}
                    disabled={isDecisionRiskOptionDisabled(code)}
                  >
                    {t(`messages.aml.titles.${code}`, {ns: 'translations'})}
                  </Radio>
                </Col>
              )}
            </Row>
          </Radio.Group>
          {selectedDecisionRisk !== 'NONE' &&
            <div style={{marginTop: 12}}>
              <div style={{marginBottom: 8}}>
                {getAvailableDecisionRiskComponents(selectedDecisionRisk).length > 1
                  ? t('forms.manager.riskGroup.multipleDescription')
                  : t('forms.manager.riskGroup.singleDescription')}
              </div>
              {getAvailableDecisionRiskComponents(selectedDecisionRisk).length > 1 &&
                <div style={{marginBottom: 10}}>
                  <span style={{marginRight: 8}}>{t('forms.manager.riskGroup.operator')}:</span>
                  <Radio.Group value={selectedDecisionOperator} onChange={event => setSelectedDecisionOperator(event.target.value)}>
                    <Radio value="OR">{t('forms.manager.riskGroup.any')} (OR)</Radio>
                    <Radio value="AND">{t('forms.manager.riskGroup.all')} (AND)</Radio>
                  </Radio.Group>
                </div>
              }
              <div>
                {getDecisionRiskComponents(decisionRiskComponent?.id?.startsWith('RISK_GUIDE_') ? riskGuideDemoSection : section).map((item, index) => {
                  const condition = selectedDecisionConditions.find(value => value.componentId === item.id)
                  const disabled = item.id !== decisionRiskComponent?.id &&
                    hasConflictingRisk(item, selectedDecisionRisk, getCurrentDecisionRiskGroupId())
                  return (
                    <div key={item.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0'}}>
                      <Checkbox
                        checked={item.id === decisionRiskComponent?.id || !!condition}
                        disabled={disabled || item.id === decisionRiskComponent?.id}
                        onChange={event => toggleDecisionRiskCondition(item.id, event.target.checked)}
                        style={{paddingRight:10}}
                      >
                        {getDecisionRiskComponentLabel(item, index)}
                      </Checkbox>
                      <Radio.Group
                        size="small"
                        value={condition?.decision}
                        disabled={!condition}
                        onChange={event => updateDecisionRiskCondition(item.id, event.target.value)}
                      >
                        <Radio value={true}>{t('forms.options.si')}</Radio>
                        <Radio value={false}>{t('forms.options.no')}</Radio>
                      </Radio.Group>
                    </div>
                  )
                })}
              </div>
            </div>
          }
        </Modal>
      }
      {isKycForm && isVisibleRiskGuide && !isVisiblePreview && !isVisibleDecisionRisk &&
        <>
          <div className="form-risk-guide-mask" aria-hidden="true">
            {!riskGuideTargetRect ?
              <div className="form-risk-guide-mask-panel" style={{top: 0, left: 0, width: '100vw', height: '100vh'}} />
              : <>
                <div className="form-risk-guide-mask-panel" style={{top: 0, left: 0, width: '100vw', height: Math.max(0, riskGuideTargetRect.top)}} />
                <div className="form-risk-guide-mask-panel" style={{top: riskGuideTargetRect.top, left: 0, width: Math.max(0, riskGuideTargetRect.left), height: riskGuideTargetRect.height}} />
                <div className="form-risk-guide-mask-panel" style={{top: riskGuideTargetRect.top, left: riskGuideTargetRect.left + riskGuideTargetRect.width, width: Math.max(0, window.innerWidth - riskGuideTargetRect.left - riskGuideTargetRect.width), height: riskGuideTargetRect.height}} />
                <div className="form-risk-guide-mask-panel" style={{top: riskGuideTargetRect.top + riskGuideTargetRect.height, left: 0, width: '100vw', height: Math.max(0, window.innerHeight - riskGuideTargetRect.top - riskGuideTargetRect.height)}} />
              </>
            }
          </div>
          {riskGuideTargetRect &&
            <div
              className="form-risk-guide-spotlight"
              style={{top: riskGuideTargetRect.top - 5, left: riskGuideTargetRect.left - 5, width: riskGuideTargetRect.width + 10, height: riskGuideTargetRect.height + 10}}
            />
          }
          <div className="form-risk-guide-card" style={getRiskGuideCardStyle()} role="dialog" aria-live="polite">
            <div className="form-risk-guide-card-header">
              <span><Icon type="safety" /> {t('forms.manager.riskGuide.title')}</span>
              <Button type="link" size="small" onClick={closeRiskGuide} aria-label={t('forms.manager.riskGuide.finish')}><Icon type="close" /></Button>
            </div>
            <div className="form-risk-guide-card-progress">{riskGuideStep + 1} / {riskGuideSteps.length}</div>
            <h3>{riskGuideSteps[riskGuideStep].title}</h3>
      {riskGuideStep === 0 ?
        <>
          <p>{riskGuideSteps[riskGuideStep].description}</p>
          <ul className="form-risk-guide-bullet-list">
            <li>{t('forms.manager.riskGuide.step1.use')} <strong>{t('forms.manager.riskGuide.step1.dataLabel')}</strong> {t('forms.manager.riskGuide.step1.dataSuffix')}</li>
            <li>{t('forms.manager.riskGuide.step1.use')} <strong>{t('forms.manager.riskGuide.step1.decisionLabel')}</strong> {t('forms.manager.riskGuide.step1.or')} <strong>{t('forms.manager.riskGuide.step1.declarationLabel')}</strong> {t('forms.manager.riskGuide.step1.decisionSuffix')}</li>
          </ul>
        </>
        : riskGuideStep === 1 ?
          <>
            <p>{riskGuideSteps[riskGuideStep].description}</p>
            <ul className="form-risk-guide-bullet-list">
              <li>
                {t('forms.manager.riskGuide.step2.typeRiskPrefix')} <strong>{t('forms.manager.fieldsetEdit.dropdown')}</strong>, <strong>{t('forms.manager.fieldsetEdit.checkbox')}</strong>, <strong>{t('forms.manager.fieldsetEdit.closedOptions')}</strong> {t('forms.manager.riskGuide.step2.typeRiskJoin')} <strong>{t('forms.manager.fieldsetEdit.optionsOther')}</strong>.
              </li>
              <li>{t('forms.manager.riskGuide.step2.typeScoringDescription')}</li>
            </ul>
          </>
        : riskGuideStep === 3 ?
          <ul className="form-risk-guide-bullet-list">
            <li>
              {t('forms.manager.riskGuide.step3.questionsPrefix')} <strong>{t('forms.manager.section.tooltip.decision')}</strong> {t('forms.manager.riskGuide.step3.questionJoin')} <strong>{t('forms.manager.section.tooltip.declaration')}</strong> {t('forms.manager.riskGuide.step3.sameSectionSuffix')}
            </li>
            <li>
              {t('forms.manager.riskGuide.step3.answerReminder')} ({t('forms.options.si')} {t('forms.manager.riskGuide.step3.questionJoin')} {t('forms.options.no')}).
            </li>
          </ul>
        : riskGuideStep === 4 ?
          <>
            <p>{t('forms.manager.riskGuide.preview.clickPrefix')} <strong>{t('forms.manager.section.preview')}</strong> {t('forms.manager.riskGuide.preview.clickSuffix')}</p>
            <ul className="form-risk-guide-bullet-list">
              <li>{t('forms.manager.riskGuide.preview.fieldsDescription')}</li>
              <li>
                {t('forms.manager.riskGuide.preview.decisionsPrefix')} <strong>{t('forms.manager.section.tooltip.decision')}</strong> {t('forms.manager.riskGuide.step3.questionJoin')} <strong>{t('forms.manager.section.tooltip.declaration')}</strong>, {t('forms.manager.riskGuide.preview.decisionsSuffix')}
              </li>
            </ul>
          </>
        : <p>{riskGuideSteps[riskGuideStep].description}</p>
      }
            {riskGuideStep === 2 && <div className="form-risk-guide-card-hint">{t('forms.manager.riskGuide.step2.tip')}</div>}
            <div className="form-risk-guide-card-actions">
              {riskGuideStep > 0 && <Button size="small" onClick={() => setRiskGuideStep(step => step - 1)}><Icon type="left" /> {t('forms.manager.riskGuide.back')}</Button>}
              <Button size="small" type="primary" onClick={() => riskGuideStep < riskGuideSteps.length - 1 ? setRiskGuideStep(step => step + 1) : closeRiskGuide()}>
                {riskGuideStep < riskGuideSteps.length - 1 ? <>{t('forms.manager.riskGuide.next')} <Icon type="right" /></> : <>{t('forms.manager.riskGuide.finish')} <Icon type="check" /></>}
              </Button>
            </div>
          </div>
        </>
      }
      { isVisiblePreview &&
        <Modal
          title={t('forms.manager.section.preview')}
          className="form-manager-preview-modal"
          footer={ null }
          visible={ true }
          onOk={ closeModalHandler  }
          onCancel={ closeModalHandler }
        >
          <div>
          <Preview section={showRiskGuideDemo ? riskGuideDemoSection : section} closeModalHandler={closeModalHandler} lang={lang} />
          </div>
        </Modal>
      }
    </div>
  )
}
export default Form.create()(SectionEdit);
